import React, { useRef, useState } from 'react';
import { Plus } from 'lucide-react';

import { Button } from '@dashboard/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@dashboard/components/ui/tooltip';
import { toast } from '@dashboard/context/ToastContext';
import usePage from '@dashboard/hooks/usePage';
import { buildPresetField, isPresetOnPage } from '@dashboard/services/pageFieldTypes';

import { useFieldEdit } from '../FieldEditContext';
import { useFields } from '../FieldsContext';
import { useFormLayout } from '../FormLayoutContext';
import { useFieldSource } from '../FieldSourceContext';

const errorMessage = error =>
  error?.errors?.[0]?.message || error?.message || (typeof error === 'string' ? error : undefined);

export default function QuickAddFields() {
  const { page } = usePage();
  const { state: fields, dispatch } = useFields();
  const { isAddingField, isReordering } = useFieldEdit();
  const { saveField, refresh, presets: fieldPresets, hasLayout, noun } = useFieldSource();
  const { hasDiscountCode, addDiscountCode } = useFormLayout();

  const [savingId, setSavingId] = useState(null);
  // State lands a render late, so a fast double click would otherwise save twice.
  const saving = useRef(false);

  const discountCodePreset = { id: 'discount-code', chipLabel: 'Discount code' };
  const presets = [
    ...(page.type === 'form' || !hasLayout ? [] : [discountCodePreset]),
    ...fieldPresets,
  ];
  if (presets.length === 0) return null;

  const busy = isAddingField || isReordering || savingId !== null;

  async function addPreset(preset) {
    if (saving.current) return;
    saving.current = true;
    setSavingId(preset.id);

    try {
      if (preset === discountCodePreset) {
        if (await addDiscountCode()) {
          toast({ title: 'Discount code added', type: 'success' });
        }
        return;
      }
      const { data, error } = await saveField(buildPresetField(preset), undefined);

      if (error || !data) {
        toast({ title: `Unable to add ${noun}`, description: errorMessage(error), type: 'error' });
        return;
      }

      dispatch({ type: 'append', values: [{ ...data, isEditing: false }] });
      refresh();
      toast({
        title: noun === 'question' ? 'Question added' : 'Field added',
        description: preset.chipLabel,
        type: 'success',
      });
    } finally {
      saving.current = false;
      setSavingId(null);
    }
  }

  return (
    <div
      role="group"
      aria-labelledby="quick-add-fields-heading"
      className="mt-8 flex flex-col gap-3"
    >
      <h3 id="quick-add-fields-heading" className="text-sm font-semibold text-foreground">
        Quick add
      </h3>

      <TooltipProvider>
        <ul className="flex flex-wrap gap-2">
          {presets.map(preset => {
            const onPage =
              preset === discountCodePreset ? hasDiscountCode : isPresetOnPage(preset, fields);

            const chip = (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-full text-xs"
                disabled={onPage || busy}
                aria-busy={savingId === preset.id || undefined}
                onClick={() => addPreset(preset)}
              >
                <Plus aria-hidden="true" />
                {preset.chipLabel}
              </Button>
            );

            return (
              <li key={preset.id} className="inline-flex">
                {onPage ? (
                  <Tooltip>
                    {/* A disabled button fires no pointer events, so the trigger needs a wrapper. */}
                    <TooltipTrigger asChild>
                      <span className="inline-flex" tabIndex={0}>
                        {chip}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {noun === 'question' ? 'Already asked' : 'Already on this form'}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  chip
                )}
              </li>
            );
          })}
        </ul>
      </TooltipProvider>
    </div>
  );
}
